import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { seDeconnecter } from '../api/sygap';
import {
  chargerSession,
  construireSession,
  effacerSession,
  enregistrerSession,
  type Session,
} from './session';

interface ValeurContexteSession {
  readonly session: Session | null;
  /** Ouvre la session à partir du jeton rendu par l'API. */
  readonly ouvrir: (jeton: string, changementRequis?: boolean) => Session | null;
  readonly fermer: () => void;
  /**
   * Lève l'obligation de changer de mot de passe, une fois le changement
   * accepté par le serveur. Le jeton courant reste valide — l'API ne ferme que
   * les **autres** sessions — donc rien à rouvrir.
   */
  readonly leverChangementRequis: () => void;
}

const ContexteSession = createContext<ValeurContexteSession | null>(null);

export function FournisseurSession({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => chargerSession());

  const ouvrir = useCallback((jeton: string, changementRequis = false) => {
    const nouvelle = construireSession(jeton, changementRequis);
    if (nouvelle === null) {
      return null;
    }
    enregistrerSession(nouvelle);
    setSession(nouvelle);
    return nouvelle;
  }, []);

  const leverChangementRequis = useCallback(() => {
    setSession((precedente) => {
      if (precedente?.changementRequis !== true) return precedente;
      const levee = { ...precedente, changementRequis: false };
      // Réécrite en stockage : sans cela, un rechargement de l'onglet
      // réimposerait l'écran alors que le mot de passe a déjà changé.
      enregistrerSession(levee);
      return levee;
    });
  }, []);

  /**
   * Ferme la session **des deux côtés**.
   *
   * Le serveur d'abord, pour que le jeton cesse d'être accepté (US-007) ;
   * effacer le `sessionStorage` seul ne prouve rien, il suffit d'avoir recopié
   * le jeton. L'effacement local suit **quoi qu'il arrive** : sur une liaison
   * coupée, laisser l'agent connecté à son écran serait pire que de fermer sans
   * avoir pu prévenir le serveur — le jeton expirera de lui-même.
   */
  const fermer = useCallback(() => {
    const jeton = session?.jeton ?? null;
    effacerSession();
    setSession(null);
    if (jeton !== null) {
      void seDeconnecter(jeton).catch(() => {
        // Rien à faire ici : la session locale est déjà close, et l'agent n'a
        // aucune action à entreprendre. Le jeton expire au pire à son échéance.
      });
    }
  }, [session]);

  const valeur = useMemo(
    () => ({ session, ouvrir, fermer, leverChangementRequis }),
    [session, ouvrir, fermer, leverChangementRequis],
  );

  return <ContexteSession.Provider value={valeur}>{children}</ContexteSession.Provider>;
}

/**
 * @throws {Error} si appelé hors du fournisseur — c'est une faute de câblage,
 * détectable au premier rendu, pas un cas métier.
 */
export function useSession(): ValeurContexteSession {
  const valeur = useContext(ContexteSession);
  if (valeur === null) {
    throw new Error('useSession doit être appelé dans un <FournisseurSession>.');
  }
  return valeur;
}
